// src/components/project/EditorApp.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Toggle from "react-toggle";
import toast from "react-hot-toast";
import EditorPreview from "./EditorPreview";

export default function EditorApp({
  draftKey,
  initialData = {},
  onSave,
  onSuccess,
  onCancel,
  showPublishToggle = true,
  minSplitPct = 25,
  maxSplitPct = 75,
  mobileBreakpointPx = 768,
}) {
  // Detect dark mode from localStorage (same as DarkModeToggle)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('i7-dark-mode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Listen for theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark-theme'));
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);
  const [state, setState] = useState(() => ({
    title: "",
    subtitle: "",
    body: "",
    technology: "",
    skill: "",
    publish: false,
    saving: false,
    apiError: null,
    errors: {},
    ...initialData,
  }));

  // --- responsive: compute after mount (no window during init) ---
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () =>
      setIsMobile(typeof window !== "undefined" && window.innerWidth < mobileBreakpointPx);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [mobileBreakpointPx]);

  const [split, setSplit] = useState(50);
  const dragging = useRef(false);
  const draftTimeout = useRef(null);

  const inputStyle = useMemo(() => ({
    backgroundColor: "#000000e6",
    color: "#59c6c1",
    borderColor: "#334155",
  }), []);

  // ---- Draft: restore (guarded) ----
  useEffect(() => {
    if (!draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const { picture_link, ...restored } = parsed;
        setState((s) => ({ ...s, ...restored, errors: {}, apiError: null }));
      }
    } catch (e) {
      console.warn("Draft restore failed", e);
    }
  }, [draftKey]);

  // ---- Draft: save (debounced + guarded) ----
  const queueDraftSave = useCallback((nextState) => {
    if (!draftKey) return;
    if (draftTimeout.current) clearTimeout(draftTimeout.current);
    draftTimeout.current = setTimeout(() => {
      try {
        const { errors, apiError, saving, ...clean } = nextState;
        localStorage.setItem(draftKey, JSON.stringify(clean));
      } catch (e) {
        console.warn("Draft save failed", e);
      }
    }, 350);
  }, [draftKey]);

  const setField = useCallback((id, value) => {
    setState((s) => {
      const next = { ...s, [id]: value };
      queueDraftSave(next);
      return next;
    });
  }, [queueDraftSave]);

  const handleChange = (e) => setField(e.target.id, e.target.value);
  const togglePublish = () => setField("publish", !state.publish);

  const clearDraftAndRefresh = () => {
    try { if (draftKey) localStorage.removeItem(draftKey); } catch {}
    if (typeof window !== "undefined") window.location.reload();
  };

  // ---- Validation ----
  const validators = {
    required: (label, v) => (!v?.trim() ? `${label} is required.` : null),
    max: (label, v, n) => (v && v.length > n ? `${label} must be ≤ ${n} characters.` : null),
  };

  const validate = useCallback((s) => {
    const errs = {};
    const add = (k, msg) => msg && (errs[k] = msg);
    add("title", validators.required("Title", s.title) || validators.max("Title", s.title, 120));
    add("subtitle", validators.max("Subtitle", s.subtitle, 200));
    add("technology", validators.max("Technology", s.technology, 120));
    add("skill", validators.max("Skill", s.skill, 120));
    add("body", validators.required("Body", s.body));
    return errs;
  }, []);

  const errors = useMemo(() => validate(state), [state, validate]);
  const hasErrors = Object.keys(errors).length > 0;

  // ---- Save (defensive) ----
  const save = async () => {
    if (typeof onSave !== "function") {
      console.error("EditorApp: onSave is not provided");
      toast.error("Save handler not configured");
      return;
    }
    setState((s) => ({ ...s, saving: true, apiError: null }));
    try {
      const payload = {
        title: state.title,
        subtitle: state.subtitle,
        body: state.body,
        technology: state.technology,
        skill: state.skill,
        publish: state.publish,
      };
      const result = await onSave(payload);
      try { if (draftKey) localStorage.removeItem(draftKey); } catch {}
      toast.success("Project saved");
      setState((s) => ({ ...s, saving: false }));
      const returnedId = result?.id ?? result?.pk;
      if (onSuccess) onSuccess(returnedId);
    } catch (err) {
      console.error("Project Save Failed", err);
      console.error("Project Save Error Response:", err.response);
      console.error("Project Save Error Status:", err.response?.status);
      console.error("Project Save Error Data:", err.response?.data);
      
      const server = err?.response?.data || {};
      let errorMessage = "Save failed";
      if (err.response?.status === 403) {
        errorMessage = "You don't have permission to save this project";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error occurred while saving project";
      } else if (err.response?.status === 400) {
        errorMessage = "Invalid project data";
      }
      toast.error(errorMessage);
      setState((s) => ({
        ...s,
        apiError: "Could not save. Fix the issues and try again.",
        errors: {
          ...s.errors,
          ...Object.fromEntries(
            Object.entries(server).map(([k, v]) => [k, Array.isArray(v) ? v.join(" ") : String(v)])
          ),
        },
        saving: false,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (hasErrors) {
      setState((s) => ({ ...s, errors, apiError: "Please fix the highlighted fields." }));
      return;
    }
    save();
  };

  // ---- Cmd/Ctrl + S ----
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!hasErrors) save();
        else setState((s) => ({ ...s, apiError: "Please fix the highlighted fields." }));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hasErrors]); // keep short; save uses latest state from closure

  // ---- Drag to resize (desktop only) ----
  useEffect(() => {
    if (isMobile) return;
    const onMove = (e) => {
      if (!dragging.current) return;
      const container = document.getElementById("pc-container");
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.min(maxSplitPct, Math.max(minSplitPct, (x / rect.width) * 100));
      setSplit(pct);
    };
    const stop = () => (dragging.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", stop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", stop);
    };
  }, [isMobile, minSplitPct, maxSplitPct]);

  const fieldClass = (key) => (errors[key] ? "pc-field pc-error" : "pc-field");
  const errText = (key) => errors[key] && <div className="pc-error-text">{errors[key]}</div>;
  const gridTemplate = isMobile ? "1fr" : `${split}% 6px ${100 - split}%`;

  const handleCancel = () => (onCancel ? onCancel() : (window.location = "/"));

  return (
    <div id="pc-container" className="pc-container">
      <div className="pc-split" style={{ gridTemplateColumns: gridTemplate }}>
        {/* LEFT: Editor */}
        <form className="pc-pane pc-form" onSubmit={handleSubmit} noValidate>
          <div className="pc-meta-header">
            <div className={fieldClass("title")}>
              <label htmlFor="title">Title</label>
              <input
                id="title"
                value={state.title}
                onChange={handleChange}
                placeholder="Punchy, concise title (renders as H1)"
                style={inputStyle}
              />
              {errText("title")}
            </div>

            <div className={fieldClass("subtitle")}>
              <label htmlFor="subtitle">Subtitle</label>
              <input
                id="subtitle"
                value={state.subtitle}
                onChange={handleChange}
                placeholder="Optional one-liner under the title"
                style={inputStyle}
              />
              {errText("subtitle")}
            </div>

            <div className="pc-meta-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <div className={fieldClass("technology")}>
                <label htmlFor="technology">Technology</label>
                <input
                  id="technology"
                  value={state.technology}
                  onChange={handleChange}
                  placeholder="e.g. PyTorch"
                  style={inputStyle}
                />
                {errText("technology")}
              </div>

              <div className={fieldClass("skill")}>
                <label htmlFor="skill">Skill</label>
                <input
                  id="skill"
                  value={state.skill}
                  onChange={handleChange}
                  placeholder="e.g. ml engineering"
                  style={inputStyle}
                />
                {errText("skill")}
              </div>
            </div>

            {state.apiError && (
              <div className="pc-alert" role="alert" style={{ marginTop: 6 }}>
                {state.apiError}
              </div>
            )}
          </div>

          <div className="pc-body-scroll">
            <div className={fieldClass("body")} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <label htmlFor="body" style={{ marginBottom: 4 }}>Body</label>
              <small className="pc-help" style={{ marginBottom: 6 }}>
                Markdown supported. Code blocks are highlighted.
              </small>
              <textarea
                id="body"
                value={state.body}
                onChange={handleChange}
                style={{ ...inputStyle, flex: "1 1 auto", minHeight: 220, resize: "vertical" }}
                placeholder={`# Introduction
Brief context, approach, and results.

\`\`\`python
# code blocks get syntax highlighting
import numpy as np
\`\`\`
`}
              />
              {errText("body")}
            </div>
          </div>

          <div className="pc-toolbar">
            <div className="pc-toolbar-left">
              {showPublishToggle && (
                <label className="pc-toggle">
                  <Toggle checked={state.publish} onChange={togglePublish} />
                  <span className="pc-status">{state.publish ? "Save as public" : "Save as private"}</span>
                </label>
              )}
            </div>

            <div className="pc-toolbar-right">
              <button className="btn btn-outline-secondary btn-sm" type="button" onClick={clearDraftAndRefresh} title="Clear local draft and refresh">
                Clear
              </button>
              <button
                className="btn btn-dark btn-sm"
                type="button"
                onClick={handleCancel}
                style={{ background: "#fff", color: "#334155", borderColor: "#e2e8f0" }}
              >
                Cancel
              </button>
              <button
                className="btn btn-dark btn-sm"
                type="submit"
                disabled={state.saving}
                style={{ background: "#59c6c1", borderColor: "#59c6c1", color: "#0f172a" }}
              >
                {state.saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </form>

        {/* Gutter / Preview only on desktop */}
        {!isMobile && <div className="pc-gutter" onMouseDown={() => (dragging.current = true)} title="Drag to resize" />}
        {!isMobile && (
          <EditorPreview
            title={state.title}
            subtitle={state.subtitle}
            technology={state.technology}
            skill={state.skill}
            body={state.body}
            publish={state.publish}
            theme={isDarkMode ? "dark" : "light"}
          />
        )}
      </div>
    </div>
  );
}
